/**
 * Account — the GLOBAL app-shell destination (renamed from "Settings",
 * owner consistency round 2026-08-25): profile + BYOK key, the per-user
 * default tier, a model and its effort together (PATCH /api/me — products
 * and sessions may override), and the guide's remembered per-repo layout
 * state.
 * Route stays /settings (frozen grammar; labels are not).
 *
 * No Preferences section: round 2 made plain English, diagram-first
 * explanation and the rest permanent server behavior rather than switches
 * ("Plain English should not be an option but always be applied").
 */

import { useMe, usePatchMe } from '../../api/queries';
import {
  ErrorNote,
  PageContainer,
  PageHeader,
  Section,
  SkeletonLine,
} from '../../lib/ui';
import type { Effort } from '../../api/types';
import { EffortMeter } from '../chat/EffortMeter';
import { effortDisplayName } from '../chat/labels';
import { ModelTierSelect } from '../chat/ModelTierSelect';
import { AccountSection, ApiKeySection } from '../products/AccountSection';

/**
 * The per-user default: a tier, which is a model and its effort together
 * (2026-09-02; the two selects it replaces are in git). A product can
 * override it from its own settings page; a single conversation can override
 * both halves from the pickers in its composer.
 */
function ModelSection() {
  const me = useMe();
  const patchMe = usePatchMe();

  if (me.isPending) return <SkeletonLine height={48} />;
  if (me.isError) return <ErrorNote>{me.error.message}</ErrorNote>;

  return (
    <div data-testid="default-model-section" className="space-y-3">
      <ModelTierSelect
        testId="default-model-select"
        ariaLabel="Default model"
        model={me.data.default_model}
        effort={me.data.default_effort}
        disabled={patchMe.isPending}
        onTier={(tier) =>
          patchMe.mutate({ default_model: tier.model, default_effort: tier.effort })
        }
        onModel={(model) => patchMe.mutate({ default_model: model })}
        footnote="Every product and conversation starts from this. Costs are bench averages on your key."
      />
      <EffortRow
        testId="default-effort-meter"
        effort={me.data.default_effort}
        inherited={me.data.effort_fallback ?? 'medium'}
        inheritWord="Server default"
        disabled={patchMe.isPending}
        onChange={(effort) => patchMe.mutate({ default_effort: effort })}
        note="Every product and conversation starts from this. Max digs deepest and is the slowest and dearest by far; the tiers above set High."
      />
      <ErrorNote>{patchMe.isError ? patchMe.error.message : null}</ErrorNote>
    </div>
  );
}

/**
 * Effort on its own row (2026-09-07). A tier writes a model AND an effort,
 * but an effort set any other way (an older UI, the composer of a product's
 * conversations) left the reader with no way to see it: an account sitting
 * on "max" unfolded the model list and said nothing about why every answer
 * took eight minutes. The row names the level in words beside the meter.
 */
export function EffortRow({
  testId,
  effort,
  inherited,
  inheritWord,
  disabled,
  onChange,
  note,
}: {
  testId: string;
  effort: Effort | null;
  /** what a cleared value resolves to */
  inherited: Effort;
  inheritWord: string;
  disabled: boolean;
  onChange: (effort: Effort | null) => void;
  note: string;
}) {
  const level = effort ?? inherited;
  return (
    <div data-testid={testId} className="flex flex-wrap items-center gap-x-3 gap-y-1">
      <span className="text-[length:var(--text-micro-size)] leading-[var(--text-micro-line)] font-medium tracking-[var(--text-micro-tracking)] uppercase text-tertiary">
        Effort
      </span>
      <EffortMeter
        testId={`${testId}-slider`}
        effort={effort}
        defaultEffort={inherited}
        onChange={onChange}
        disabled={disabled}
        ariaLabel="Default effort"
        inheritWord={inheritWord}
      />
      {effort === null ? (
        <span data-testid={`${testId}-level`} className="text-tertiary">
          {`follows the ${inheritWord.toLowerCase()} (${effortDisplayName(level).toLowerCase()})`}
        </span>
      ) : null}
      <p className="mt-0 mb-0 basis-full text-[length:var(--text-micro-size)] leading-[var(--text-micro-line)] text-tertiary">
        {note}
      </p>
    </div>
  );
}

export function SettingsPage() {
  return (
    <PageContainer testId="settings-page">
      <PageHeader
        title="Account"
        meta="You, and the key and model that power the guide."
      />
      <div className="space-y-6">
        <Section title="Profile" testId="settings-account">
          <AccountSection />
        </Section>
        <Section
          title="API key"
          testId="settings-api-key"
          description="loremap runs on your Anthropic key: your key, your bill. Refreshes show their price before they run and every reply prints what it cost. The key is stored on this computer, never in your code, and never sent anywhere but Anthropic."
        >
          <ApiKeySection />
        </Section>
        <Section
          title="Model"
          testId="settings-model"
          description="The tier the guide runs on everywhere: a model and how hard it thinks. A product or a single conversation can override it, and a conversation can turn its own effort up or down from its composer."
        >
          <ModelSection />
        </Section>
      </div>
    </PageContainer>
  );
}
